import { StylePropertiesTypes } from '@wix/document-services-types'
import type { Spx } from '@wix/thunderbolt-becky-types'
import type { StylePropItem } from '@wix/thunderbolt-becky-root'
import { getConverter } from '../utils/convertersRegistry'
import { getStringFromConverter } from '../utils/converterHelpers'

/** Internal Converters */
const coordinatesConverter = (prop: StylePropertiesTypes.Coordinates<StylePropItem>, spx: Spx) => {
	const { top: topProp, right: rightProp, bottom: bottomProp, left: leftProp } = prop

	const topConverter = topProp && getConverter(topProp.type)
	const top = topProp && topConverter && getStringFromConverter(topConverter(topProp, spx))

	const rightConverter = rightProp && getConverter(rightProp.type)
	const right = rightProp && rightConverter && getStringFromConverter(rightConverter(rightProp, spx))

	const bottomConverter = bottomProp && getConverter(bottomProp.type)
	const bottom = bottomProp && bottomConverter && getStringFromConverter(bottomConverter(bottomProp, spx))

	const leftConverter = leftProp && getConverter(leftProp.type)
	const left = leftProp && leftConverter && getStringFromConverter(leftConverter(leftProp, spx))

	return {
		top,
		right,
		bottom,
		left,
		[prop.type]: `var(--{propName}-top) var(--{propName}-right) var(--{propName}-bottom) var(--{propName}-left)`,
	}
}

const dimensionsConverter = (prop: StylePropertiesTypes.Dimensions<StylePropItem>, spx: Spx) => {
	const { horizontal: horizontalProp, vertical: verticalProp } = prop

	const horizontalConverter = getConverter(horizontalProp.type)
	const horizontal = horizontalConverter && getStringFromConverter(horizontalConverter(horizontalProp, spx))

	const verticalConverter = getConverter(verticalProp.type)
	const vertical = verticalConverter && getStringFromConverter(verticalConverter(verticalProp, spx))

	return `${horizontal} ${vertical}`
}

const cornersConverter = (prop: StylePropertiesTypes.Corners<StylePropItem>, spx: Spx) => {
	const {
		topLeft: topLeftProp,
		topRight: topRightProp,
		bottomRight: bottomRightProp,
		bottomLeft: bottomLeftProp,
	} = prop

	const topLeftConverter = topLeftProp && getConverter(topLeftProp.type)
	const topLeft = topLeftProp && topLeftConverter && getStringFromConverter(topLeftConverter(topLeftProp, spx))

	const topRightConverter = topRightProp && getConverter(topRightProp.type)
	const topRight = topRightProp && topRightConverter && getStringFromConverter(topRightConverter(topRightProp, spx))

	const bottomRightConverter = bottomRightProp && getConverter(bottomRightProp.type)
	const bottomRight =
		bottomRightProp && bottomRightConverter && getStringFromConverter(bottomRightConverter(bottomRightProp, spx))

	const bottomLeftConverter = bottomLeftProp && getConverter(bottomLeftProp.type)
	const bottomLeft =
		bottomLeftProp && bottomLeftConverter && getStringFromConverter(bottomLeftConverter(bottomLeftProp, spx))

	return {
		topLeft,
		topRight,
		bottomRight,
		bottomLeft,
		[prop.type]: `var(--{propName}-topLeft) var(--{propName}-topRight) var(--{propName}-bottomRight) var(--{propName}-bottomLeft)`,
	}
}

const isComplexList = (
	prop: StylePropertiesTypes.List<StylePropItem | string>
): prop is StylePropertiesTypes.List<StylePropItem> => typeof prop.value[0] !== 'string'

const listConverter = (prop: StylePropertiesTypes.List<StylePropItem | string>, spx?: Spx) => {
	if (isComplexList(prop)) {
		return prop.value
			.map((item) => {
				const converter = getConverter(item.type)
				const convertedStyleProp = converter?.(item, spx)

				if (typeof convertedStyleProp === 'string') {
					return convertedStyleProp
				}

				if (!convertedStyleProp || typeof convertedStyleProp !== 'object') {
					return ''
				}

				const mainStylePropArray = convertedStyleProp[item.type]?.split(' ').map((cssVar: string) => {
					if (cssVar.includes('var(--{propName}-')) {
						const key = cssVar.split('var(--{propName}-')[1].split(')')[0]
						return convertedStyleProp[key]
					}
					return cssVar
				})

				return mainStylePropArray?.filter(Boolean).join(' ') || ''
			})
			.join(', ')
	}

	return prop.value.map((item) => ((item as string).includes(' ') ? `'${item}'` : item)).join(', ')
}

/** External Map */
export const genericsConverterMap = {
	Coordinates: coordinatesConverter,
	Corners: cornersConverter,
	Dimensions: dimensionsConverter,
	List: listConverter,
}
